import React from 'react';
import {Box, Divider, IconButton, Stack, Tooltip} from "@mui/material";
import {alpha} from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ReportEditor from "./reportEditor/ReportEditor";
import LeftPanel from "./leftPanel/LeftPanel";
import {useReportStore} from "../useReportStore";
import DisclosureEditor from "../disclosureEditorPage/disclosureEditor/DisclosureEditor";
import DisclosureLeftPanel from "../disclosureEditorPage/leftPanel/DisclosureLeftPanel";
import PitchEditor from "../pitchEditorPage/pitchEditor/PitchEditor";
import PitchLeftPanel from "../pitchEditorPage/leftPanel/PitchLeftPanel";
import {useGenerateInitialOnMount} from "./hooks/useGenerateInitialOnMount";
import useEditorChatShortcut from './hooks/useEditorChatShortcut';
import useResizablePanelWidth from '../../../hooks/useResizablePanelWidth';

const LEFT_PANEL_BY_TYPE = {
    disclosure: DisclosureLeftPanel,
    pitch: PitchLeftPanel,
};

const EDITOR_BY_TYPE = {
    disclosure: DisclosureEditor,
    pitch: PitchEditor,
};

const PANEL_WIDTH = 360;
const PANEL_MIN_WIDTH = 260;
const PANEL_MAX_WIDTH = 760;
const COLLAPSED_STRIP_WIDTH = 28;
const TOGGLE_SIZE = 24;

/**
 * ReportEditorPage - Smart editor page that renders appropriate editor based on report type
 * - For regular reports: uses ReportEditor with LeftPanel
 * - For disclosure reports: uses DisclosureEditor with DisclosureLeftPanel
 * - For pitch reports: uses PitchEditor with PitchLeftPanel
 *
 * Modals (SourcePackageBuilder, GlobalGenerateInitialVersionModal) are rendered
 * in the parent ReportPageLayout to prevent re-renders when editor content changes.
 */
const ReportEditorPage = () => {
    const rteRef = React.useRef(null);
    const setRteRef = useReportStore(s => s.setRteRef);
    const productType = useReportStore(s => s.productType);
    const productTypeValue = productType?.value;
    const [collapsed, setCollapsed] = React.useState(false);
    const {width: panelWidth, isResizing, onResizeStart} = useResizablePanelWidth({
        storageKey: 'reportEditor.leftPanelWidth',
        defaultWidth: PANEL_WIDTH,
        min: PANEL_MIN_WIDTH,
        max: PANEL_MAX_WIDTH,
    });

    // Check if this is a disclosure report
    const isDisclosure = productTypeValue === 'disclosure';

    // Store the ref in Zustand when component mounts
    React.useEffect(() => {
        setRteRef(rteRef);
        return () => {
            setRteRef(null);
        };
    }, [setRteRef]);

    // Handle generateInitial URL param for disclosures (opens modal on mount)
    useGenerateInitialOnMount(isDisclosure);
    useEditorChatShortcut();

    const LeftPanelComponent = LEFT_PANEL_BY_TYPE[productTypeValue] || LeftPanel;
    const EditorComponent = EDITOR_BY_TYPE[productTypeValue] || ReportEditor;

    return (
        <Stack direction={'row'} height={`calc(100vh - 64px)`} flexGrow={1} sx={{overflow: 'hidden'}}>

            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    flexShrink: 0,
                    overflow: 'visible',
                    width: collapsed ? COLLAPSED_STRIP_WIDTH : panelWidth,
                    transition: isResizing ? 'none' : 'width 0.2s ease',
                }}
            >
                <Stack
                    sx={{
                        width: collapsed ? 0 : panelWidth,
                        overflow: 'hidden',
                        transition: isResizing ? 'none' : 'width 0.2s ease',
                        flexShrink: 0,
                    }}
                >
                    <LeftPanelComponent/>
                </Stack>

                {/* Drag-to-resize handle on the panel's right edge (hidden while collapsed) */}
                {!collapsed && (
                    <Box
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize left panel"
                        onPointerDown={onResizeStart}
                        sx={(theme) => ({
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            right: -3,
                            width: 6,
                            zIndex: 15,
                            cursor: 'col-resize',
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.25),
                            },
                            ...(isResizing && {
                                bgcolor: alpha(theme.palette.primary.main, 0.4),
                            }),
                        })}
                    />
                )}

                {/* Collapse / expand toggle pinned to the divider edge */}
                <Tooltip title={collapsed ? 'Expand panel' : 'Collapse panel'} placement="right" arrow>
                    <IconButton
                        size="small"
                        onClick={() => setCollapsed((c) => !c)}
                        aria-label={collapsed ? 'Expand panel' : 'Collapse panel'}
                        sx={(theme) => ({
                            position: 'absolute',
                            top: 52,
                            zIndex: 20,
                            width: TOGGLE_SIZE,
                            height: TOGGLE_SIZE,
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '50%',
                            boxShadow: theme.shadows[2],
                            ...(collapsed
                                ? {
                                    left: (COLLAPSED_STRIP_WIDTH - TOGGLE_SIZE) / 2,
                                    right: 'auto',
                                }
                                : {
                                    left: 'auto',
                                    right: -TOGGLE_SIZE / 2,
                                }),
                            '&:hover': {bgcolor: 'action.hover'},
                            '& .MuiSvgIcon-root': {fontSize: '1rem'},
                        })}
                    >
                        {collapsed ? <ChevronRightIcon/> : <ChevronLeftIcon/>}
                    </IconButton>
                </Tooltip>
            </Box>

            <Divider orientation={'vertical'}/>

            <EditorComponent />
        </Stack>
    );
};

export default ReportEditorPage;